import { Icon } from "@mdi/react";
import { useEffect, useState } from "react";

import { mdiAlert, mdiAlertCircle, mdiClose, mdiInformation } from "@mdi/js";

import { useSystemStatusSelector } from "hooks/useSystemStatusSelector";

import { SystemStatusResponse } from "api/systemStatus";

import {
  BannerContainer,
  BannerContent,
  DismissButton,
  StatusIcon,
  StatusMessage,
  StatusText,
  StatusType
} from "./SystemStatusBanner.styles";

interface SystemStatusBannerProps {
  /** Whether to show banner for all status types or only critical ones */
  showOnlyForErrors?: boolean;
  /** Custom CSS class */
  className?: string;
}

const SystemStatusBanner = ({
  showOnlyForErrors = false,
  className
}: SystemStatusBannerProps) => {
  const { currentStatus } = useSystemStatusSelector();
  const [dismissed, setDismissed] = useState(false);
  const [lastStatusKey, setLastStatusKey] = useState<string>("");

  // Reset dismissed state when status changes or appears/disappears
  useEffect(() => {
    const currentStatusKey = currentStatus
      ? `${currentStatus.type}:${currentStatus.message}`
      : "none";

    if (currentStatusKey !== lastStatusKey) {
      setDismissed(false);
      setLastStatusKey(currentStatusKey);
    }
  }, [currentStatus, lastStatusKey]);

  const shouldShowBanner = () => {
    if (!currentStatus || dismissed) {
      return false;
    }

    if (showOnlyForErrors) {
      return currentStatus.type === "major";
    }

    // Show for all status types (major, partial, planned)
    return true;
  };

  const getStatusIcon = (status: SystemStatusResponse) => {
    switch (status.type) {
      case "major":
        return mdiAlert;
      case "partial":
        return mdiAlertCircle;
      case "planned":
        return mdiInformation;
      default:
        return mdiInformation;
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  if (!shouldShowBanner()) {
    return null;
  }

  return (
    <BannerContainer className={className} statusType={currentStatus.type}>
      <BannerContent>
        <StatusIcon>
          <Icon path={getStatusIcon(currentStatus)} size="20px" />
        </StatusIcon>
        <StatusMessage>
          <StatusText>{currentStatus.message}</StatusText>
          <StatusType>{currentStatus.type} Outage</StatusType>
        </StatusMessage>
        <DismissButton onClick={handleDismiss} aria-label="Dismiss notification">
          <Icon path={mdiClose} size="18px" />
        </DismissButton>
      </BannerContent>
    </BannerContainer>
  );
};

export default SystemStatusBanner;
